import React, {
    createContext,
    Dispatch,
    PropsWithChildren,
    SetStateAction,
    useContext,
    useMemo,
    useState,
} from 'react';
import { PRODUCT_TYPES, SUPPORTED_UNITS } from '@schuettflix/interfaces';
import { FieldValues } from 'react-hook-form';
import { ProcessingType } from '@/shared/models/ContextDataModel/interfaces/enums/processing-type.ts';
import { ContextData, ContextDataInterface } from '@/shared/models/ContextDataModel/ContextData.ts';
import { getProcessingTypeFromEntity } from '@/shared/utils/getProcessingTypeFromEntity.ts';
import { GetPositionConfigurationResponse } from '@/shared/types/positionV2/api/get-position-configuration.ts';
import { prefillAutomaticOrderForm } from '@/shared/forms/AutomaticProductsForm/domain/prefillAutomaticOrderForm.ts';

type OrderBaseFormValues = {
    quantity?: number;
    productName?: string;
    billingUnit?: SUPPORTED_UNITS;
    numberOfTransports?: number;
    productType?: PRODUCT_TYPES;
    isStandalone?: boolean;
    disposalPurpose?: 'RECYCLING' | 'DISPOSAL';
};

type OrderContextFormValues = OrderBaseFormValues & FieldValues;

type OrderContextType = {
    processingType: ProcessingType;
    setProcessingType: Dispatch<SetStateAction<ProcessingType>>;
    contextData: ContextDataInterface<OrderContextFormValues>;
} & (
    | { isPositionOrder: true; position: GetPositionConfigurationResponse }
    | { isPositionOrder: false; position: undefined }
);

const OrderContext = createContext<OrderContextType | null>(null);

type OrderContextProviderProps = PropsWithChildren & {
    positionV2?: GetPositionConfigurationResponse;
};

export const OrderContextProvider: React.FC<OrderContextProviderProps> = ({ positionV2, children }) => {
    const currentProcessingType = getProcessingTypeFromEntity(positionV2);
    const [processingType, setProcessingType] = useState<ProcessingType>(currentProcessingType ?? 'AUTOMATIC');

    const contextData = useMemo(
        () => {
            const internalContextData = new ContextData<OrderContextFormValues>({
                customerInformation: positionV2
                    ? {
                          constructionProjectId: positionV2.customer.constructionProjectId,
                          organizationId: positionV2.customer.organizationId,
                          userId: positionV2.customer.userId,
                          projectNumber: positionV2.customer.projectNumber,
                          manualCostCenter: null,
                      }
                    : undefined,
                businessCase: positionV2?.businessCase,
                financeSettings: positionV2?.financeSettings,
            });

            if (positionV2 && processingType === 'AUTOMATIC') {
                internalContextData.setFormData(
                    prefillAutomaticOrderForm(positionV2.businessCase.pricingStrategy, positionV2.productConfigurations)
                );
            }

            return internalContextData;
        },
        // NOTE: positionV2 is not a stable value,
        // which causes contextData to reinitialise, so we use `position?.id` instead
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [positionV2?.id]
    );

    return (
        <OrderContext.Provider
            value={{
                processingType,
                setProcessingType,
                contextData,
                ...(positionV2
                    ? { isPositionOrder: true, position: positionV2 }
                    : { isPositionOrder: false, position: undefined }),
            }}
        >
            {children}
        </OrderContext.Provider>
    );
};

export function useOrderContext() {
    const data = useContext(OrderContext);
    if (data === null) {
        throw new Error('OrderContextProvider needs to be present in node tree');
    }
    return data;
}
